import { Link } from "react-router-dom"
import { Layout } from "../components/Layout"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { CommsTabs, ChannelPill } from "@/components/CommsTabs"
import { useCommsTriage, useSetThreadResolved, formatAge, daysAgo } from "@/hooks/useComms"
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  FireIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline"

function HealthStat({
  label,
  value,
  hint,
  to,
}: {
  label: string
  value: string
  hint?: string
  to?: string
}) {
  const body = (
    <>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold text-primary">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </>
  )
  const base = "block rounded-lg border border-border bg-surface px-4 py-3"
  return to ? (
    <Link to={to} className={`${base} transition-colors hover:border-primary/40 hover:bg-muted`}>
      {body}
    </Link>
  ) : (
    <div className={base}>{body}</div>
  )
}

export function CommsTriage() {
  const { data, isLoading, error } = useCommsTriage()
  const setResolved = useSetThreadResolved()

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const owed = data?.owedReply ?? []
  const cold = data?.goingCold ?? []
  const health = data?.health

  const syncDays = daysAgo(health?.syncCursor?.updated_at ?? null)
  const syncHint =
    syncDays === null ? "never synced" : syncDays <= 1 ? "synced recently" : `${syncDays}d ago`

  return (
    <Layout>
      <div className="space-y-6">
        <CommsTabs />
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Comms · Triage</h1>
            <p className="text-muted-foreground">
              {isLoading ? "Loading…" : "What needs your attention, oldest first."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-4 text-sm">
            <Link to="/comms/crop-waitlist" className="text-blue-600 hover:underline">
              Crop waitlist →
            </Link>
            <Link to="/comms/cheer-waitlist" className="text-blue-600 hover:underline">
              Cheer list →
            </Link>
            <Link to="/comms/orgs" className="text-blue-600 hover:underline">
              Organizations →
            </Link>
          </div>
        </div>

        {/* Capture health */}
        {health && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <HealthStat label="Contacts" value={String(health.totals.contacts)} to="/comms/contacts" />
            <HealthStat label="Threads" value={String(health.totals.threads)} to="/comms/threads" />
            <HealthStat label="Messages" value={String(health.totals.messages)} to="/comms/messages" />
            <HealthStat
              label="Needs classify"
              value={String(health.needsClassification)}
              hint={health.needsClassification > 0 ? "run comms-sync" : "all classified"}
              to="/comms/messages?bucket=needs_classify"
            />
            <HealthStat
              label="Auto-snoozed"
              value={String(health.autoSnoozed)}
              hint="no reply in 21d; wake on reply"
              to="/comms/contacts"
            />
            <HealthStat label="Sent-sync" value={syncHint} />
          </div>
        )}

        {/* Owed a reply */}
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-primary">
            <ClockIcon className="h-5 w-5 text-muted-foreground" />
            Owed a reply
            <span className="text-sm font-normal text-muted-foreground">({owed.length})</span>
          </h2>
          {isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
          ) : owed.length === 0 ? (
            <EmptyState
              icon={<CheckCircleIcon className="h-10 w-10" />}
              title="Inbox zero"
              description="No threads are waiting on a reply."
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {owed.map((t) => (
                <li key={t.thread_public_id} className="flex items-center hover:bg-muted">
                  <Link
                    to={`/comms/threads/${t.thread_public_id}`}
                    className="flex min-w-0 flex-1 items-center justify-between px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-primary">
                        {t.contact?.display_name || t.contact?.primary_email || "Unknown sender"}
                        {t.contact?.org && (
                          <span className="ml-2 text-xs text-muted-foreground">· {t.contact.org}</span>
                        )}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">{t.subject || "(no subject)"}</p>
                    </div>
                    <span className="ml-4 flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                      <ChannelPill channel={t.channel} isLegal={t.is_legal} />
                      {formatAge(t.last_inbound_at)}
                    </span>
                  </Link>
                  {/* Handled elsewhere (phone, text)? Archive it off the list. A newer
                      reply from them reopens it automatically. */}
                  <button
                    type="button"
                    disabled={setResolved.isPending}
                    onClick={() => setResolved.mutate({ publicId: t.thread_public_id, resolved: true })}
                    className="mr-4 shrink-0 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-primary disabled:opacity-50"
                  >
                    Archive
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Going cold */}
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-primary">
            <FireIcon className="h-5 w-5 text-muted-foreground" />
            Going cold
            <span className="text-sm font-normal text-muted-foreground">
              ({cold.length}, relevant &amp; quiet {data?.coldDays ?? 21}d+)
            </span>
          </h2>
          {isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
          ) : cold.length === 0 ? (
            <EmptyState
              icon={<ChatBubbleLeftRightIcon className="h-10 w-10" />}
              title="Nobody's gone cold"
              description="Every relevant contact has been touched recently."
            />
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {cold.map((c) => (
                <li key={c.public_id}>
                  <Link
                    to={`/comms/contacts/${c.public_id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-primary">
                        {c.display_name || c.primary_email}
                        {c.org && <span className="ml-2 text-xs text-muted-foreground">· {c.org}</span>}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">{c.primary_email}</p>
                    </div>
                    <span className="ml-4 shrink-0 text-xs text-muted-foreground">
                      {formatAge(c.last_contacted_at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  )
}
